<template>
  <el-dialog
    title="Edycja inwestycji"
    :visible="visible"
    width="780px"
    :close-on-click-modal="false"
    @update:visible="$emit('update:visible', $event)"
  >
    <div v-loading="loading">
      <el-form ref="form" :model="form" :rules="rules" label-position="top">
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="Nazwa inwestycji" prop="name">
              <el-input v-model="form.name" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="Nazwa dewelopera">
              <el-input v-model="form.developer_name" maxlength="255" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="Typ inwestycji" prop="type">
              <el-select v-model="form.type" style="width: 100%;">
                <el-option label="Mieszkaniowa" value="residential" />
                <el-option label="Komercyjna (lokale użytkowe)" value="commercial" />
                <el-option label="Mieszana" value="mixed" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Etap budowy" prop="build_status">
              <el-select v-model="form.build_status" style="width: 100%;">
                <el-option label="W planach" value="planned" />
                <el-option label="W budowie" value="under_construction" />
                <el-option label="Gotowe do odbioru" value="ready" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Termin oddania">
              <el-date-picker
                v-model="form.delivery_date"
                type="date"
                value-format="yyyy-MM-dd"
                format="yyyy-MM-dd"
                placeholder="Wybierz datę"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Opis" prop="description">
          <el-input v-model="form.description" type="textarea" :rows="6" maxlength="17000" />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="6">
            <el-form-item label="Liczba budynków">
              <el-input-number v-model="form.buildings_count" :min="0" :max="65535" controls-position="right" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Liczba klatek">
              <el-input-number v-model="form.staircases_count" :min="0" :max="65535" controls-position="right" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Liczba pięter">
              <el-input-number v-model="form.floors_count" :min="0" :max="65535" controls-position="right" style="width: 100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Liczba lokali">
              <el-input-number v-model="form.total_units" :min="0" :max="65535" controls-position="right" style="width: 100%;" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="Standard wykończenia">
              <el-input v-model="form.finishing_standard" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Technologia budowy">
              <el-input v-model="form.construction_technology" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Ogrzewanie">
              <el-input v-model="form.heating" maxlength="255" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="Osoba kontaktowa">
              <el-input v-model="form.contact_person" maxlength="255" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Telefon kontaktowy">
              <el-input v-model="form.contact_phone" maxlength="50" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="E-mail kontaktowy" prop="contact_email">
              <el-input v-model="form.contact_email" maxlength="255" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Link do wirtualnego spaceru">
          <el-input v-model="form.virtual_tour_url" maxlength="2000" />
        </el-form-item>
      </el-form>

      <p class="hint">
        Status inwestycji zmieniasz na liście. Lokalizacja (opis i współrzędne), zdjęcia,
        wizualizacje, rzuty, dokumenty, udogodnienia i import lokali pozostają w panelu dewelopera.
      </p>
    </div>

    <span slot="footer">
      <el-button @click="$emit('update:visible', false)">Anuluj</el-button>
      <el-button type="primary" :loading="saving" @click="save">Zapisz zmiany</el-button>
    </span>
  </el-dialog>
</template>

<script>
import InvestmentResource from '@/api/investment';

const investmentResource = new InvestmentResource();

/**
 * Edycja danych podstawowych inwestycji (PUT admin/investments/{id}).
 * Zestaw pól = whitelist AdminInvestmentUpdateRequest - celowo bez zdjęć,
 * dokumentów, udogodnień i lokali (to zostaje w panelu dewelopera) oraz bez
 * statusu (zmienia go select na liście / changeStatus).
 *
 * Lokalizacji (location_name/lat/lon) NIE ma ani w formularzu, ani w payloadzie:
 * ustawia ją deweloper razem z geokodowaniem, a ręczne wpisanie współrzędnych
 * rozjeżdża opis lokalizacji z punktem na mapie. Klucze muszą pozostać poza
 * EMPTY_FORM - inaczej poleciałyby puste i skasowały poprawne dane.
 */
const EMPTY_FORM = {
  name: '',
  developer_name: '',
  type: 'residential',
  build_status: 'under_construction',
  delivery_date: null,
  buildings_count: undefined,
  staircases_count: undefined,
  floors_count: undefined,
  total_units: undefined,
  finishing_standard: '',
  construction_technology: '',
  heating: '',
  description: '',
  virtual_tour_url: '',
  contact_person: '',
  contact_phone: '',
  contact_email: '',
};

export default {
  name: 'InvestmentEditDialog',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    investmentId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      saving: false,
      form: { ...EMPTY_FORM },
      rules: {
        name: { required: true, message: 'Nazwa jest wymagana', trigger: 'blur' },
        type: { required: true, message: 'Typ jest wymagany', trigger: 'change' },
        build_status: { required: true, message: 'Etap budowy jest wymagany', trigger: 'change' },
        description: { required: true, message: 'Opis jest wymagany', trigger: 'blur' },
        contact_email: { type: 'email', message: 'Nieprawidłowy adres e-mail', trigger: 'blur' },
      },
    };
  },
  watch: {
    visible(open) {
      if (open) {
        this.load();
      }
    },
  },
  methods: {
    async load() {
      if (!this.investmentId) {
        return;
      }
      this.loading = true;
      // Czyścimy formularz PRZED requestem: bez tego nieudane pobranie zostawiało
      // dane poprzednio otwartej inwestycji, a „Zapisz zmiany" nadpisywało nimi
      // inwestycję, której dane nigdy się nie wczytały.
      this.form = { ...EMPTY_FORM };
      try {
        const investment = await investmentResource.get(this.investmentId);
        const form = { ...EMPTY_FORM };
        Object.keys(EMPTY_FORM).forEach((key) => {
          const value = investment[key];
          if (value !== undefined && value !== null) {
            form[key] = value;
          }
        });
        this.form = form;
        if (this.$refs.form) {
          this.$refs.form.clearValidate();
        }
      } catch (error) {
        this.$notify({ title: 'Błąd', message: 'Nie udało się pobrać danych inwestycji', type: 'error', duration: 3000 });
        this.$emit('update:visible', false);
      } finally {
        this.loading = false;
      }
    },
    save() {
      this.$refs.form.validate(async(valid) => {
        if (!valid) {
          return;
        }
        this.saving = true;
        try {
          // Puste liczby wysyłamy jako null (backend: nullable|integer),
          // pusty string zostałby zamieniony na null i tak - trzymamy to jawnie.
          const payload = { ...this.form };
          ['buildings_count', 'staircases_count', 'floors_count', 'total_units'].forEach((key) => {
            if (payload[key] === undefined || payload[key] === '') {
              payload[key] = null;
            }
          });
          await investmentResource.update(this.investmentId, payload);
          this.$notify({ title: 'Sukces', message: 'Inwestycja została zapisana', type: 'success', duration: 2000 });
          this.$emit('update:visible', false);
          this.$emit('saved');
        } finally {
          this.saving = false;
        }
      });
    },
  },
};
</script>

<style scoped>
.hint {
  font-size: 12px;
  color: #909399;
  margin: 0;
}
</style>
